import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  DollarSign,
  TrendingUp,
  Users,
  PieChart,
  Download,
  Plus,
  Edit2,
  Trash2,
  Settings as SettingsIcon,
  CheckCircle2,
  AlertCircle,
  Calendar,
  Receipt,
  Briefcase,
  Ticket,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const money = (n) =>
  (n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

const AdminAccountingPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview'); // overview | subs | expenses | settings

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const [s, e] = await Promise.all([
        axios.get(`${API}/admin/accounting/summary?months=12`, { headers }),
        axios.get(`${API}/admin/accounting/expenses`, { headers }),
      ]);
      setSummary(s.data);
      setExpenses(e.data);
    } catch (err) {
      toast.error('Failed to load accounting data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadCsv = async () => {
    try {
      const year = new Date().getFullYear();
      const res = await axios.get(`${API}/admin/accounting/export.csv?year=${year}`, {
        headers,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audiostride-${year}-tax.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  if (loading || !summary) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
      </div>
    );
  }

  const tm = summary.this_month || {};
  const activeSubs = (summary.subs || []).filter(
    (s) => s.status === 'active' && !s.cancel_at_period_end
  );
  const cancelingSubs = (summary.subs || []).filter(
    (s) => s.status === 'active' && s.cancel_at_period_end
  );
  const tabs = [
    { id: 'overview', label: 'Overview', icon: PieChart },
    { id: 'subs', label: 'Analytics subscribers', icon: Briefcase },
    { id: 'expenses', label: 'Expenses', icon: Receipt },
    { id: 'settings', label: 'Settings', icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="admin-accounting-page">
      <header className="bg-gradient-to-r from-teal-500 to-blue-600 text-white py-6 px-4">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-white/90 hover:text-white mb-4"
            data-testid="back-to-admin"
          >
            <ArrowLeft className="w-5 h-5" /> Back to Dashboard
          </button>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-3xl font-black flex items-center gap-3">
                <DollarSign className="w-8 h-8" /> Accounting
              </h1>
              <p className="text-teal-50 mt-1">
                Revenue, Stripe fees, expenses, and your tax-ready monthly P&amp;L — all in one place.
              </p>
            </div>
            <button
              onClick={downloadCsv}
              data-testid="export-csv-btn"
              className="bg-white text-blue-700 px-5 py-3 rounded-full font-bold shadow-lg hover:scale-105 transition flex items-center gap-2"
            >
              <Download className="w-5 h-5" />
              Export {new Date().getFullYear()} CSV
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* KPI cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi
            icon={TrendingUp}
            label="Monthly recurring revenue"
            value={money(summary.mrr)}
            sub={`${summary.subscribers.active_count + summary.subscribers.canceling_count} subscriptions · ${money(summary.arr)} ARR`}
            accent="from-teal-500 to-blue-600"
            testid="mrr-card"
          />
          <Kpi
            icon={Receipt}
            label={`${tm.label || 'This month'} — net`}
            value={money(tm.net || 0)}
            sub={`${money(tm.revenue_total)} revenue − ${money(tm.fees)} fees − ${money(tm.expenses)} expenses`}
            accent="from-emerald-500 to-teal-500"
            testid="this-month-card"
          />
          <Kpi
            icon={Briefcase}
            label="Analytics subscribers"
            value={`${summary.subscribers.active_count}`}
            sub={`${summary.subscribers.canceling_count} canceling · ${summary.subscribers.churned_count} churned`}
            accent="from-blue-500 to-indigo-600"
            testid="subscriber-card"
            onClick={() => setTab('subs')}
            bodyContent={
              activeSubs.length === 0 ? (
                <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
                  No B2B Visitor Intelligence subscribers yet.
                </div>
              ) : (
                <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                    Active — B2B Visitor Intelligence
                  </div>
                  {activeSubs.slice(0, 3).map((s) => (
                    <div key={s.subscription_id} className="text-xs">
                      <div className="font-bold text-slate-800 truncate">{s.org_name}</div>
                      <div className="text-slate-500 truncate">{s.contact_name} · {s.contact_email}</div>
                    </div>
                  ))}
                  {cancelingSubs.length > 0 && (
                    <div className="text-[11px] text-amber-700 font-bold pt-1">
                      ⚠ {cancelingSubs.length} canceling — see full list
                    </div>
                  )}
                  {activeSubs.length > 3 && (
                    <div className="text-[11px] text-blue-600 font-bold pt-1">
                      + {activeSubs.length - 3} more · view all →
                    </div>
                  )}
                </div>
              )
            }
          />
          <Kpi
            icon={PieChart}
            label="Net (last 12 months)"
            value={money(summary.totals.net)}
            sub={`${money(summary.totals.revenue)} revenue − ${money(summary.totals.fees + summary.totals.expenses)} costs`}
            accent="from-orange-500 to-rose-500"
            testid="totals-card"
          />
        </div>

        {/* Tabs */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="flex border-b border-slate-200 overflow-x-auto">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-bold whitespace-nowrap transition ${
                  tab === t.id
                    ? 'text-blue-700 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {tab === 'overview' && <OverviewTab summary={summary} />}
            {tab === 'subs' && <SubsTab summary={summary} />}
            {tab === 'expenses' && (
              <ExpensesTab expenses={expenses} settings={summary.settings} token={token} onChange={load} />
            )}
            {tab === 'settings' && <SettingsTab settings={summary.settings} token={token} onChange={load} />}
          </div>
        </div>
      </main>
    </div>
  );
};

const Kpi = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid={testid}>
    <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-gradient-to-r ${accent} text-white text-xs font-bold uppercase tracking-wider mb-3`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className="text-3xl font-black text-slate-900">{value}</div>
    <div className="text-xs text-slate-600 mt-1">{sub}</div>
  </div>
);

const OverviewTab = ({ summary }) => (
  <div className="space-y-6">
    <div>
      <h3 className="text-lg font-black text-slate-900 mb-3">Last 12 months</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
          <thead className="bg-slate-50">
            <tr className="text-xs uppercase tracking-wider text-slate-600">
              <Th>Month</Th>
              <Th align="right"><Briefcase className="w-3.5 h-3.5 inline mr-1" />Subs</Th>
              <Th align="right"><Ticket className="w-3.5 h-3.5 inline mr-1" />Tours</Th>
              <Th align="right">Revenue</Th>
              <Th align="right">Fees</Th>
              <Th align="right">Expenses</Th>
              <Th align="right">Net</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {summary.series.slice().reverse().map((m) => (
              <tr key={m.month} className="hover:bg-slate-50">
                <Td>{m.label}</Td>
                <Td align="right" muted={!m.subscription_revenue}>{money(m.subscription_revenue)}</Td>
                <Td align="right" muted={!m.tour_revenue}>{money(m.tour_revenue)}</Td>
                <Td align="right" bold>{money(m.revenue_total)}</Td>
                <Td align="right" muted>-{money(m.fees)}</Td>
                <Td align="right" muted>-{money(m.expenses)}</Td>
                <Td align="right" bold tone={m.net > 0 ? 'emerald' : m.net < 0 ? 'rose' : ''}>
                  {money(m.net)}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const SubsTab = ({ summary }) => {
  const subs = summary.subs || [];
  if (subs.length === 0) {
    return (
      <div className="space-y-4">
        <SubsHeader />
        <Empty text="No B2B Visitor Intelligence subscribers yet. Create your first offer at Admin → B2B Subscriptions." />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <SubsHeader />
      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
          <thead className="bg-slate-50">
            <tr className="text-xs uppercase tracking-wider text-slate-600">
              <Th>Organization</Th>
              <Th>Contact</Th>
              <Th>Tour</Th>
              <Th>Plan</Th>
              <Th align="right">Price</Th>
              <Th>Started</Th>
              <Th>Next bill</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {subs.map((s) => {
              const label =
                s.status === 'canceled'
                  ? 'Canceled'
                  : s.cancel_at_period_end
                  ? 'Canceling'
                  : s.status === 'active'
                  ? 'Active'
                  : s.status;
              const tone =
                s.status === 'active' && !s.cancel_at_period_end
                  ? 'emerald'
                  : s.cancel_at_period_end
                  ? 'amber'
                  : 'slate';
              return (
                <tr key={s.subscription_id} className="hover:bg-slate-50" data-testid={`sub-row-${s.subscription_id}`}>
                  <Td>
                    <div className="font-bold text-slate-900">{s.org_name}</div>
                  </Td>
                  <Td>
                    <div className="text-slate-900">{s.contact_name}</div>
                    <div className="text-xs text-slate-500">{s.contact_email}</div>
                  </Td>
                  <Td>{s.tour_title}</Td>
                  <Td>
                    <span className="capitalize">{s.tier}</span> · {s.report_cadence}
                  </Td>
                  <Td align="right" bold>
                    {money(s.price_usd)}
                    <span className="text-xs text-slate-500 font-normal"> /{s.billing_interval === 'yearly' ? 'yr' : 'mo'}</span>
                  </Td>
                  <Td>{(s.started_at || '').slice(0, 10)}</Td>
                  <Td>{(s.current_period_end || '').slice(0, 10)}</Td>
                  <Td>
                    <Pill tone={tone}>{label}</Pill>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SubsHeader = () => (
  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
    <div className="flex items-start gap-3">
      <Briefcase className="w-5 h-5 text-blue-700 mt-0.5 flex-shrink-0" />
      <div>
        <p className="font-bold text-blue-900 text-sm">Analytics subscribers — B2B Visitor Intelligence</p>
        <p className="text-xs text-blue-800 mt-1">Museums, cities and attractions paying for recurring visitor-behaviour reports. This list is <strong>separate from tour purchasers</strong> — tour one-off sales appear in the Overview revenue column.</p>
      </div>
    </div>
  </div>
);

const ExpensesTab = ({ expenses, settings, token, onChange }) => {
  const today = new Date().toISOString().slice(0, 10);
  const empty = {
    date: today,
    vendor: '',
    description: '',
    amount_usd: '',
    category: settings.categories?.[0] || 'Other',
    recurring: false,
    notes: '',
  };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const save = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount_usd || !form.category) {
      toast.error('Description, amount and category are required');
      return;
    }
    setBusy(true);
    try {
      const body = { ...form, amount_usd: Number(form.amount_usd) };
      if (editingId) {
        await axios.put(`${API}/admin/accounting/expenses/${editingId}`, body, { headers });
        toast.success('Expense updated');
      } else {
        await axios.post(`${API}/admin/accounting/expenses`, body, { headers });
        toast.success('Expense added');
      }
      setForm(empty);
      setEditingId(null);
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const edit = (e) => {
    setEditingId(e.expense_id);
    setForm({
      date: (e.date || today).slice(0, 10),
      vendor: e.vendor || '',
      description: e.description || '',
      amount_usd: e.amount_usd,
      category: e.category,
      recurring: !!e.recurring,
      notes: e.notes || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const del = async (id) => {
    if (!window.confirm('Delete this expense? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/accounting/expenses/${id}`, { headers });
      toast.success('Deleted');
      onChange();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={save} className="bg-slate-50 rounded-xl border border-slate-200 p-5 space-y-4" data-testid="expense-form">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-slate-900">{editingId ? 'Edit expense' : 'Add expense'}</h3>
          {editingId && (
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setForm(empty);
              }}
              className="text-xs font-bold text-slate-500 hover:text-slate-700"
            >
              Cancel edit
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Date" required>
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2" required data-testid="expense-date" />
          </Field>
          <Field label="Vendor">
            <input type="text" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="Resend, Stripe, etc." className="w-full border border-slate-300 rounded-lg px-3 py-2" data-testid="expense-vendor" />
          </Field>
          <Field label="Amount (USD)" required>
            <input type="number" step="0.01" min="0.01" value={form.amount_usd} onChange={(e) => setForm({ ...form, amount_usd: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2" required data-testid="expense-amount" />
          </Field>
          <Field label="Category" required>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white" required data-testid="expense-category">
              {(settings.categories || []).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Field>
          <Field label="Description" required spans={2}>
            <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="April hosting · domain renewal · etc." className="w-full border border-slate-300 rounded-lg px-3 py-2" required data-testid="expense-description" />
          </Field>
          <Field label="Notes" spans={3}>
            <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2" data-testid="expense-notes" />
          </Field>
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} data-testid="expense-recurring" />
            This is a recurring expense
          </label>
          <button type="submit" disabled={busy} data-testid="expense-submit" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-teal-500 to-blue-600 hover:opacity-90 text-white font-bold disabled:opacity-50">
            <Plus className="w-4 h-4" />
            {busy ? 'Saving…' : editingId ? 'Update expense' : 'Add expense'}
          </button>
        </div>
      </form>

      <div>
        <h3 className="text-lg font-black text-slate-900 mb-3">All expenses</h3>
        {expenses.length === 0 ? (
          <Empty text="No expenses yet. Add your first one above — even the $0.30 Stripe debit adds up at tax time." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
              <thead className="bg-slate-50">
                <tr className="text-xs uppercase tracking-wider text-slate-600">
                  <Th>Date</Th>
                  <Th>Vendor</Th>
                  <Th>Description</Th>
                  <Th>Category</Th>
                  <Th align="right">Amount</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {expenses.map((e) => (
                  <tr key={e.expense_id} className="hover:bg-slate-50" data-testid={`expense-row-${e.expense_id}`}>
                    <Td>{(e.date || '').slice(0, 10)}</Td>
                    <Td>{e.vendor || <span className="text-slate-400">—</span>}</Td>
                    <Td>
                      <div className="font-bold text-slate-900">{e.description}</div>
                      {e.notes && <div className="text-xs text-slate-500">{e.notes}</div>}
                    </Td>
                    <Td><Pill tone="slate">{e.category}{e.recurring ? ' · recurring' : ''}</Pill></Td>
                    <Td align="right" bold tone="rose">-{money(e.amount_usd)}</Td>
                    <Td align="right">
                      <button onClick={() => edit(e)} data-testid={`edit-expense-${e.expense_id}`} className="text-slate-500 hover:text-blue-600 p-1.5 rounded">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => del(e.expense_id)} data-testid={`delete-expense-${e.expense_id}`} className="text-slate-500 hover:text-rose-600 p-1.5 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const SettingsTab = ({ settings, token, onChange }) => {
  const [feePct, setFeePct] = useState(settings.stripe_fee_percent);
  const [feeFix, setFeeFix] = useState(settings.stripe_fee_fixed);
  const [newCat, setNewCat] = useState('');
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const saveFees = async () => {
    setBusy(true);
    try {
      await axios.put(
        `${API}/admin/accounting/settings`,
        { stripe_fee_percent: Number(feePct), stripe_fee_fixed: Number(feeFix) },
        { headers }
      );
      toast.success('Stripe fee rates updated');
      onChange();
    } catch {
      toast.error('Failed to update fees');
    } finally {
      setBusy(false);
    }
  };

  const addCategory = async () => {
    const trimmed = newCat.trim();
    if (!trimmed) return;
    const next = [...(settings.custom_categories || []), trimmed];
    try {
      await axios.put(`${API}/admin/accounting/settings`, { custom_categories: next }, { headers });
      toast.success(`Added "${trimmed}"`);
      setNewCat('');
      onChange();
    } catch {
      toast.error('Failed to add category');
    }
  };

  const removeCategory = async (c) => {
    const next = (settings.custom_categories || []).filter((x) => x !== c);
    try {
      await axios.put(`${API}/admin/accounting/settings`, { custom_categories: next }, { headers });
      toast.success(`Removed "${c}"`);
      onChange();
    } catch {
      toast.error('Failed to remove category');
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-black text-slate-900 mb-2 flex items-center gap-2">
          <DollarSign className="w-5 h-5" /> Stripe fee rate
        </h3>
        <p className="text-sm text-slate-600 mb-4">
          Defaults: 2.9% + $0.30 per successful charge (US standard). Check your Stripe statement and adjust if your actual rate differs — this feeds into the "fees" column everywhere.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Percent">
            <input type="number" step="0.01" min="0" max="10" value={feePct} onChange={(e) => setFeePct(e.target.value)} className="w-32 border border-slate-300 rounded-lg px-3 py-2" data-testid="fee-percent-input" />
          </Field>
          <Field label="Fixed (USD)">
            <input type="number" step="0.01" min="0" max="5" value={feeFix} onChange={(e) => setFeeFix(e.target.value)} className="w-32 border border-slate-300 rounded-lg px-3 py-2" data-testid="fee-fixed-input" />
          </Field>
          <button onClick={saveFees} disabled={busy} data-testid="save-fees-btn" className="px-5 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold disabled:opacity-50">
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-black text-slate-900 mb-2 flex items-center gap-2">
          <Plus className="w-5 h-5" /> Custom expense categories
        </h3>
        <p className="text-sm text-slate-600 mb-4">Defaults are always available. Add any custom categories your accountant prefers.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {(settings.categories || []).map((c) => {
            const isCustom = (settings.custom_categories || []).includes(c);
            return (
              <span key={c} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold ${isCustom ? 'bg-teal-100 text-teal-800 border border-teal-200' : 'bg-slate-100 text-slate-700'}`}>
                {c}
                {isCustom && (
                  <button onClick={() => removeCategory(c)} data-testid={`remove-cat-${c}`} title="Remove custom category" className="text-slate-500 hover:text-rose-600">
                    ×
                  </button>
                )}
              </span>
            );
          })}
        </div>
        <div className="flex gap-2">
          <input type="text" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="e.g., Advertising, Bank fees" className="flex-1 border border-slate-300 rounded-lg px-3 py-2" data-testid="new-category-input" />
          <button onClick={addCategory} disabled={!newCat.trim()} data-testid="add-category-btn" className="px-5 py-2.5 rounded-full bg-teal-500 hover:bg-teal-600 text-white font-bold disabled:opacity-50">
            Add
          </button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 text-sm text-slate-700">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-bold text-blue-900">Everything is auto-saved for tax purposes.</p>
            <p className="mt-1">Every subscription, tour purchase and expense is stored in your database the moment it happens. The "Export CSV" button at the top gives you one combined file per year with all transactions categorized — hand it to your accountant as-is.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- helpers ---
const Th = ({ children, align = 'left' }) => (
  <th className={`px-4 py-3 text-${align} font-bold`}>{children}</th>
);
const Td = ({ children, align = 'left', bold, muted, tone }) => {
  const toneClass =
    tone === 'emerald'
      ? 'text-emerald-700'
      : tone === 'rose'
      ? 'text-rose-700'
      : tone === 'amber'
      ? 'text-amber-700'
      : 'text-slate-900';
  return (
    <td className={`px-4 py-3 text-${align} ${bold ? 'font-bold ' + toneClass : muted ? 'text-slate-500' : 'text-slate-900'}`}>
      {children}
    </td>
  );
};
const Field = ({ label, required, children, spans }) => (
  <label className={`block ${spans ? `md:col-span-${spans}` : ''}`}>
    <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
      {label} {required && <span className="text-rose-500">*</span>}
    </span>
    <div className="mt-1">{children}</div>
  </label>
);
const Pill = ({ children, tone = 'slate' }) => {
  const cls = {
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    amber: 'bg-amber-100 text-amber-800 border-amber-300',
    rose: 'bg-rose-100 text-rose-800 border-rose-300',
    slate: 'bg-slate-100 text-slate-700 border-slate-300',
  }[tone];
  return (
    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold border ${cls}`}>{children}</span>
  );
};
const Empty = ({ text }) => (
  <div className="bg-slate-50 rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-600 flex items-center justify-center gap-2">
    <AlertCircle className="w-5 h-5 text-slate-400" /> {text}
  </div>
);

export default AdminAccountingPage;
